import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  adminFetchStats,
  adminFetchContributions,
  adminUpdateStatus,
  adminFetchContributionsMapData,
  adminFetchSegmentsGeoJson,
  type AdminContributionRow,
} from '../api/participation-api';

// ── Config ────────────────────────────────────────────────────────────────────

const SCLC_PROJECT_ID = '5ccb8229-6533-4732-adbf-29c27aadd639';
const SCLC_CENTER: [number, number] = [-92.638, 16.737];
const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_API_KEY as string;

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ['pending', 'reviewed', 'accepted', 'archived'] as const;

const STATUS_LABELS: Record<string, string> = {
  pending:  'Pendiente',
  reviewed: 'Revisado',
  accepted: 'Aceptado',
  archived: 'Archivado',
};

const STATUS_COLORS: Record<string, string> = {
  pending:  '#f59e0b',
  reviewed: '#3b82f6',
  accepted: '#22c55e',
  archived: '#9ca3af',
};

const SCALE_LABELS: Record<string, string> = {
  city:   'Ciudad completa',
  sector: 'Sector urbano',
  place:  'Calle o lugar',
};

// One color per category (HSL-based palette)
const CATEGORY_COLORS: Record<string, string> = {
  'Agua, drenaje e infraestructura':        '#3b82f6',
  'Movilidad y transporte':                 '#f97316',
  'Espacio público y accesibilidad':        '#22c55e',
  'Medio ambiente, humedales y riesgos':    '#14b8a6',
  'Vivienda, suelo y crecimiento urbano':   '#eab308',
  'Patrimonio, cultura e identidad':        '#a855f7',
  'Seguridad y convivencia':                '#ef4444',
  'Economía local, turismo y comercio':     '#ec4899',
  'Residuos y limpieza urbana':             '#78716c',
  'Otro':                                   '#64748b',
};

function categoryColor(cat: string): string {
  return CATEGORY_COLORS[cat] ?? '#94a3b8';
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: '2-digit', year: '2-digit',
  });
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const inputStyle: CSSProperties = {
  padding: '7px 10px', border: '1.5px solid #e0e0e0', borderRadius: 8,
  fontSize: 12.5, color: '#374151', background: '#fff', cursor: 'pointer',
  outline: 'none', minWidth: 0,
};

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  label, value, accent, subtitle,
}: { label: string; value: number | string; accent?: string; subtitle?: string }): JSX.Element {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '18px 22px',
      border: '1.5px solid #f0f0f0', flex: '1 1 130px', minWidth: 120,
      borderTop: accent ? `3px solid ${accent}` : undefined,
    }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: '#1a1a2e', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6, fontWeight: 500 }}>{label}</div>
      {subtitle && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}

// ── Bar chart row ─────────────────────────────────────────────────────────────

function BarRow({
  label, value, total, color, onClick,
}: { label: string; value: number; total: number; color?: string; onClick?: () => void }): JSX.Element {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div
      onClick={onClick}
      style={{ marginBottom: 8, cursor: onClick ? 'pointer' : undefined }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 12.5, color: '#374151', fontWeight: 500, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 8 }}>{value} <span style={{ color: '#c4c9d4' }}>({pct}%)</span></span>
      </div>
      <div style={{ height: 5, borderRadius: 3, background: '#f0f0f0' }}>
        <div style={{ height: 5, borderRadius: 3, width: `${pct}%`, background: color ?? '#1a1a2e', transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

// ── Detail panel ──────────────────────────────────────────────────────────────

interface DetailPanelProps {
  row: AdminContributionRow;
  projectId: string;
  onClose: () => void;
  onStatusChange: (id: string, status: string) => void;
}

function DetailPanel({ row, projectId, onClose, onStatusChange }: DetailPanelProps): JSX.Element {
  const [status, setStatus] = useState<string>(row.status);
  const [saving, setSaving] = useState(false);

  async function handleStatusChange(newStatus: string): Promise<void> {
    if (newStatus === status) return;
    setSaving(true);
    try {
      await adminUpdateStatus(projectId, row.id, newStatus);
      setStatus(newStatus);
      onStatusChange(row.id, newStatus);
    } finally {
      setSaving(false);
    }
  }

  const field = (label: string, val: string | null | undefined): JSX.Element | null => {
    if (!val) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
        <div style={{ fontSize: 13.5, color: '#1a1a2e', lineHeight: 1.6, wordBreak: 'break-word' }}>{val}</div>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 440,
      background: '#fff', boxShadow: '-8px 0 40px rgba(0,0,0,0.1)',
      overflowY: 'auto', zIndex: 1000, padding: '24px 28px 40px',
      borderLeft: '1px solid #f0f0f0',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Detalle del aporte</h3>
        <button type="button" onClick={onClose} aria-label="Cerrar"
          style={{ width: 30, height: 30, borderRadius: '50%', border: '1.5px solid #e8e8e8', background: '#f9f9f9', cursor: 'pointer', fontSize: 13, color: '#9ca3af' }}>
          ✕
        </button>
      </div>

      {/* Status control */}
      <div style={{ marginBottom: 20, padding: '12px 14px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e8e8e8' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Estado</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STATUS_OPTIONS.map(s => (
            <button key={s} type="button" disabled={saving}
              onClick={() => void handleStatusChange(s)}
              style={{
                padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                border: `1.5px solid ${status === s ? STATUS_COLORS[s] : '#e0e0e0'}`,
                background: status === s ? STATUS_COLORS[s] : '#fff',
                color: status === s ? '#fff' : '#555',
                transition: 'all 0.15s',
              }}>
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {field('ID', row.id)}
      {field('Escala', SCALE_LABELS[row.scale] ?? row.scale)}
      {field('Categoría', row.category)}
      {field('Modo', row.accessible_mode ? 'Accesible (sin mapa)' : 'Mapa')}
      {field('Fecha', fmtDate(row.created_at))}
      {field('Comentario', row.comment)}
      {field('Descripción de ubicación', row.location_description)}
      {field('Ámbito de referencia', row.zone_name)}

      {/* Naming context */}
      {row.street_label && (
        <div style={{ marginTop: 6, marginBottom: 14, padding: '12px 14px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Segmento nombrado</div>
          {field('Calle', row.street_label)}
          {row.from_street && row.to_street && (
            <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.5 }}>
              entre <strong>{row.from_street}</strong> y <strong>{row.to_street}</strong>
            </div>
          )}
          {row.naming_confidence && (
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
              Confianza: {row.naming_confidence}
            </div>
          )}
        </div>
      )}

      {row.geometry_geojson && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>Geometría</div>
          <div style={{ fontSize: 11.5, color: '#6b7280', fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {(row.geometry_geojson as { type?: string }).type ?? 'GeoJSON'}
          </div>
        </div>
      )}

      {/* Contact — only if authorized */}
      {row.contact_requested && (
        <div style={{ marginTop: 18, padding: '14px 16px', background: '#eff6ff', borderRadius: 12, border: '1px solid #bfdbfe' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Datos de contacto</div>
          {field('Nombre', row.contact_name)}
          {field('Correo', row.contact_email)}
          {field('Teléfono', row.contact_phone)}
        </div>
      )}
    </div>
  );
}

// ── Analytics map ─────────────────────────────────────────────────────────────

interface MapLayerState {
  contributions:  boolean;
  segments:       boolean;
  accepted:       boolean;
}

interface AnalyticsMapProps {
  projectId: string;
  filters: {
    category?: string;
    scale?: string;
    status?: string;
  };
  onSelectContribution: (id: string) => void;
}

function AnalyticsMap({ projectId, filters, onSelectContribution }: AnalyticsMapProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<maplibregl.Map | null>(null);
  const [layers, setLayers]   = useState<MapLayerState>({ contributions: true, segments: true, accepted: true });
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  // Init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    try {
      const map = new maplibregl.Map({
        container: containerRef.current,
        style: `https://api.maptiler.com/maps/dataviz-light/style.json?key=${MAPTILER_KEY}`,
        center: SCLC_CENTER,
        zoom: 12.5,
        attributionControl: { compact: true },
      });
      map.addControl(new maplibregl.NavigationControl(), 'top-right');
      mapRef.current = map;

      map.once('load', () => {
        // Boundary
        fetch('/api/v1/public/projects/sclc/boundary')
          .then(r => r.json())
          .then(boundary => {
            if (!mapRef.current) return;
            map.addSource('boundary', { type: 'geojson', data: boundary as GeoJSON.GeoJSON });
            map.addLayer({ id: 'boundary-fill', type: 'fill', source: 'boundary', paint: { 'fill-color': '#1a1a2e', 'fill-opacity': 0.04 } });
            map.addLayer({ id: 'boundary-line', type: 'line', source: 'boundary', paint: { 'line-color': '#1a1a2e', 'line-width': 1.5, 'line-opacity': 0.4 } });
          })
          .catch(() => undefined);

        // Segment layer (lines)
        map.addSource('segments', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({
          id: 'segments-line', type: 'line', source: 'segments',
          paint: {
            'line-color': ['case', ['>', ['get', 'accepted_count'], 0], '#22c55e', '#3b82f6'],
            'line-width': ['interpolate', ['linear'], ['get', 'contribution_count'], 1, 2, 5, 5, 10, 8],
            'line-opacity': 0.7,
          },
        });

        // Contributions layer (circles)
        map.addSource('contributions', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        map.addLayer({
          id: 'contributions-circle', type: 'circle', source: 'contributions',
          filter: ['!=', ['get', 'status'], 'accepted'],
          paint: {
            'circle-radius': 6,
            'circle-color': ['match', ['get', 'category'],
              'Agua, drenaje e infraestructura',     '#3b82f6',
              'Movilidad y transporte',               '#f97316',
              'Espacio público y accesibilidad',     '#22c55e',
              'Medio ambiente, humedales y riesgos', '#14b8a6',
              'Vivienda, suelo y crecimiento urbano','#eab308',
              'Patrimonio, cultura e identidad',      '#a855f7',
              'Seguridad y convivencia',              '#ef4444',
              'Economía local, turismo y comercio',  '#ec4899',
              'Residuos y limpieza urbana',           '#78716c',
              '#64748b',
            ],
            'circle-stroke-color': '#fff',
            'circle-stroke-width': 1.5,
            'circle-opacity': 0.85,
          },
        });

        // Accepted layer (highlighted)
        map.addLayer({
          id: 'accepted-circle', type: 'circle', source: 'contributions',
          filter: ['==', ['get', 'status'], 'accepted'],
          paint: {
            'circle-radius': 9,
            'circle-color': '#22c55e',
            'circle-stroke-color': '#fff',
            'circle-stroke-width': 2.5,
            'circle-opacity': 0.95,
          },
        });

        // Click: contributions
        map.on('click', 'contributions-circle', e => {
          const f = e.features?.[0];
          if (f?.properties?.id) onSelectContribution(f.properties.id as string);
        });
        map.on('click', 'accepted-circle', e => {
          const f = e.features?.[0];
          if (f?.properties?.id) onSelectContribution(f.properties.id as string);
        });

        // Cursors
        for (const layer of ['contributions-circle', 'accepted-circle', 'segments-line']) {
          map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
          map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
        }

        setMapReady(true);
      });
    } catch (err) {
      setMapError(err instanceof Error ? err.message : 'Error al inicializar el mapa');
    }

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load data when ready or filters change
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;

    void adminFetchContributionsMapData(projectId, filters)
      .then(data => {
        const src = map.getSource('contributions') as maplibregl.GeoJSONSource | undefined;
        src?.setData(data as unknown as GeoJSON.GeoJSON);
      })
      .catch(() => undefined);
  }, [mapReady, projectId, filters.category, filters.scale, filters.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;

    void adminFetchSegmentsGeoJson(projectId)
      .then(data => {
        const src = map.getSource('segments') as maplibregl.GeoJSONSource | undefined;
        src?.setData(data as unknown as GeoJSON.GeoJSON);
      })
      .catch(() => undefined);
  }, [mapReady, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync layer visibility
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;
    const vis = (v: boolean) => (v ? 'visible' : 'none') as 'visible' | 'none';
    if (map.getLayer('contributions-circle')) map.setLayoutProperty('contributions-circle', 'visibility', vis(layers.contributions));
    if (map.getLayer('accepted-circle'))      map.setLayoutProperty('accepted-circle',      'visibility', vis(layers.accepted));
    if (map.getLayer('segments-line'))        map.setLayoutProperty('segments-line',         'visibility', vis(layers.segments));
  }, [mapReady, layers]);

  const toggle = (key: keyof MapLayerState) => setLayers(prev => ({ ...prev, [key]: !prev[key] }));

  const layerToggleStyle = (active: boolean): CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
    borderRadius: 8, border: `1.5px solid ${active ? '#1a1a2e' : '#e0e0e0'}`,
    background: active ? '#1a1a2e' : '#fff',
    color: active ? '#fff' : '#374151',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.15s',
  });

  return (
    <div style={{ position: 'relative' }}>
      {/* Layer toggles */}
      <div style={{
        position: 'absolute', top: 12, left: 12, zIndex: 10,
        background: 'rgba(255,255,255,0.96)', borderRadius: 12,
        padding: '12px 14px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
        display: 'flex', flexDirection: 'column', gap: 6, minWidth: 210,
      }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>Capas</div>

        <button type="button" style={layerToggleStyle(layers.contributions)} onClick={() => toggle('contributions')}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />
          Aportes georreferenciados
        </button>

        <button type="button" style={layerToggleStyle(layers.segments)} onClick={() => toggle('segments')}>
          <span style={{ width: 16, height: 3, borderRadius: 2, background: '#3b82f6', flexShrink: 0 }} />
          Segmentos con aportes
        </button>

        <button type="button" style={layerToggleStyle(layers.accepted)} onClick={() => toggle('accepted')}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
          Aportes aceptados
        </button>

        <div style={{ marginTop: 4, padding: '7px 12px', borderRadius: 8, border: '1.5px solid #e0e0e0', background: '#f9fafb', color: '#c4c9d4', fontSize: 11.5, fontWeight: 600, cursor: 'not-allowed' }}>
          <span style={{ marginRight: 6 }}>—</span>
          Sintaxis espacial
          <div style={{ fontSize: 10, color: '#c4c9d4', fontWeight: 400, marginTop: 2 }}>Sin datos de modelo sintáctico</div>
        </div>
      </div>

      {/* Map container */}
      {mapError ? (
        <div style={{ height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fef2f2', borderRadius: 14, border: '1.5px solid #fecaca', color: '#dc2626', fontSize: 13 }}>
          Error al cargar el mapa: {mapError}
        </div>
      ) : (
        <div ref={containerRef} style={{ width: '100%', height: 520, borderRadius: 14, overflow: 'hidden' }} />
      )}

      {/* Legend */}
      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 4px' }}>
        {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
          <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6b7280' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
            {cat}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface ContributionsAdminPageProps {
  hideInternalHeader?: boolean;
}

export function ContributionsAdminPage({ hideInternalHeader = false }: ContributionsAdminPageProps): JSX.Element {
  const { projectId: rawId } = useParams<{ projectId?: string }>();
  const projectId = rawId ?? SCLC_PROJECT_ID;

  const [stats,    setStats]    = useState<Record<string, unknown> | null>(null);
  const [rows,     setRows]     = useState<AdminContributionRow[]>([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState<AdminContributionRow | null>(null);
  const [page,     setPage]     = useState(1);
  const [activeTab, setActiveTab] = useState<'list' | 'map'>('list');
  const PAGE_SIZE = 50;

  // Filters
  const [fCategory,      setFCategory]      = useState('');
  const [fScale,         setFScale]         = useState('');
  const [fStatus,        setFStatus]        = useState('');
  const [fContact,       setFContact]       = useState('');
  const [fMode,          setFMode]          = useState('');
  const [fDateFrom,      setFDateFrom]      = useState('');
  const [fDateTo,        setFDateTo]        = useState('');
  const [fReferenceArea, setFReferenceArea] = useState('');
  const [fText,          setFText]          = useState('');
  const [textInput,      setTextInput]      = useState('');

  const hasFilters = !!(fScale || fStatus || fContact || fMode || fDateFrom || fDateTo || fReferenceArea || fText || fCategory);

  function clearFilters(): void {
    setFCategory(''); setFScale(''); setFStatus(''); setFContact('');
    setFMode(''); setFDateFrom(''); setFDateTo(''); setFReferenceArea('');
    setFText(''); setTextInput(''); setPage(1);
  }

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        adminFetchStats(projectId),
        adminFetchContributions(projectId, {
          category:      fCategory      || undefined,
          scale:         fScale         || undefined,
          status:        fStatus        || undefined,
          referenceArea: fReferenceArea || undefined,
          text:          fText          || undefined,
          hasContact:    fContact === 'true' ? true : fContact === 'false' ? false : undefined,
          accessibleMode: fMode === 'true' ? true : fMode === 'false' ? false : undefined,
          dateFrom:      fDateFrom || undefined,
          dateTo:        fDateTo   || undefined,
          page,
          pageSize: PAGE_SIZE,
        }),
      ]);
      setStats(s);
      setRows(c.data);
      setTotal(c.total);
    } catch { /* stale */ } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadData(); }, [page, fCategory, fScale, fStatus, fContact, fMode, fDateFrom, fDateTo, fReferenceArea, fText]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleStatusChange(id: string, newStatus: string): void {
    setRows(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
    if (selected?.id === id) setSelected(s => s ? { ...s, status: newStatus } : s);
  }

  function openDetail(row: AdminContributionRow): void {
    setSelected(row);
    setActiveTab('list');
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const categoryStats    = stats?.byCategory    as Record<string, number> | undefined;
  const scaleStats       = stats?.byScale       as Record<string, number> | undefined;
  const referenceStats   = stats?.byReferenceArea as Record<string, number> | undefined;
  const recentRows       = stats?.recent        as Array<{ id: string; category: string; scale: string; status: string; comment: string | null; created_at: string }> | undefined;

  const statTotal       = (stats?.total       as number) ?? 0;
  const statPending     = (stats?.pending     as number) ?? 0;
  const statReviewed    = (stats?.reviewed    as number) ?? 0;
  const statAccepted    = (stats?.accepted    as number) ?? 0;
  const statWithContact = (stats?.withContact as number) ?? 0;

  const mapFilters = { category: fCategory || undefined, scale: fScale || undefined, status: fStatus || undefined };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Segoe UI', system-ui, sans-serif", color: '#1a1a2e' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      {!hideInternalHeader && <div style={{ background: '#1a1a2e', color: '#fff', padding: '18px 32px', display: 'flex', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Resultados de la consulta ciudadana
          </div>
          <h1 style={{ margin: '2px 0 0', fontSize: 20, fontWeight: 800, lineHeight: 1.15 }}>
            San Cristóbal de las Casas
          </h1>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <a href={`/api/v1/admin/projects/${projectId}/contributions/export.csv`}
            style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.25)', color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
            ↓ CSV
          </a>
          <a href={`/api/v1/admin/projects/${projectId}/contributions/export.geojson`}
            style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.25)', color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
            ↓ GeoJSON
          </a>
        </div>
      </div>}

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '28px 24px', paddingRight: selected ? 468 : 24 }}>

        {/* ── Summary cards ──────────────────────────────────────────────────── */}
        {stats && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <StatCard label="Total aportes" value={statTotal} />
            <StatCard label="Pendientes" value={statPending} accent={STATUS_COLORS['pending']} />
            <StatCard label="Revisados" value={statReviewed} accent={STATUS_COLORS['reviewed']} />
            <StatCard label="Aceptados" value={statAccepted} accent={STATUS_COLORS['accepted']} />
            <StatCard label="Con contacto autorizado" value={statWithContact} accent="#6366f1" />
            {scaleStats && Object.entries(scaleStats).map(([k, v]) => (
              <StatCard key={k} label={SCALE_LABELS[k] ?? k} value={v} />
            ))}
          </div>
        )}

        {/* ── Breakdowns ─────────────────────────────────────────────────────── */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 20 }}>

            {/* Category */}
            {categoryStats && Object.keys(categoryStats).length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', border: '1.5px solid #f0f0f0' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Por categoría</div>
                {Object.entries(categoryStats).sort((a, b) => b[1] - a[1]).map(([cat, cnt]) => (
                  <BarRow key={cat} label={cat} value={cnt} total={statTotal} color={categoryColor(cat)}
                    onClick={() => { setFCategory(fCategory === cat ? '' : cat); setPage(1); }} />
                ))}
              </div>
            )}

            {/* Reference area */}
            {referenceStats && Object.keys(referenceStats).length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', border: '1.5px solid #f0f0f0' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Por ámbito de referencia</div>
                {Object.entries(referenceStats).sort((a, b) => b[1] - a[1]).map(([area, cnt]) => (
                  <BarRow key={area} label={area} value={cnt} total={statTotal} color="#6366f1"
                    onClick={() => { setFReferenceArea(fReferenceArea === area ? '' : area); setPage(1); }} />
                ))}
              </div>
            )}

            {/* Recent */}
            {recentRows && recentRows.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', border: '1.5px solid #f0f0f0' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>Últimos aportes</div>
                {recentRows.map(r => (
                  <div key={r.id} style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-start' }}>
                    <span style={{ padding: '2px 7px', borderRadius: 12, fontSize: 10.5, fontWeight: 700, background: STATUS_COLORS[r.status] + '22', color: STATUS_COLORS[r.status], flexShrink: 0 }}>
                      {STATUS_LABELS[r.status] ?? r.status}
                    </span>
                    <div>
                      <div style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>{r.category}</div>
                      {r.comment && <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 2, lineHeight: 1.4 }}>{r.comment}</div>}
                      <div style={{ fontSize: 10.5, color: '#c4c9d4', marginTop: 3 }}>{fmtShortDate(r.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Filters ────────────────────────────────────────────────────────── */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '14px 18px', marginBottom: 16, border: '1.5px solid #f0f0f0', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', alignSelf: 'center' }}>Filtros</div>

          <select style={inputStyle} value={fCategory} onChange={e => { setFCategory(e.target.value); setPage(1); }}>
            <option value="">Todas las categorías</option>
            {Object.keys(CATEGORY_COLORS).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select style={inputStyle} value={fScale} onChange={e => { setFScale(e.target.value); setPage(1); }}>
            <option value="">Todas las escalas</option>
            <option value="city">Ciudad</option>
            <option value="sector">Sector</option>
            <option value="place">Calle/lugar</option>
          </select>
          <select style={inputStyle} value={fStatus} onChange={e => { setFStatus(e.target.value); setPage(1); }}>
            <option value="">Todos los estados</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          <select style={inputStyle} value={fContact} onChange={e => { setFContact(e.target.value); setPage(1); }}>
            <option value="">Contacto: todos</option>
            <option value="true">Con contacto</option>
            <option value="false">Sin contacto</option>
          </select>
          <select style={inputStyle} value={fMode} onChange={e => { setFMode(e.target.value); setPage(1); }}>
            <option value="">Modo: todos</option>
            <option value="false">Mapa</option>
            <option value="true">Accesible</option>
          </select>

          {/* Reference area */}
          {referenceStats && Object.keys(referenceStats).length > 0 && (
            <select style={inputStyle} value={fReferenceArea} onChange={e => { setFReferenceArea(e.target.value); setPage(1); }}>
              <option value="">Todos los ámbitos</option>
              {Object.keys(referenceStats).map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          )}

          <input type="date" style={inputStyle} value={fDateFrom} onChange={e => { setFDateFrom(e.target.value); setPage(1); }} title="Desde" />
          <input type="date" style={inputStyle} value={fDateTo}   onChange={e => { setFDateTo(e.target.value);   setPage(1); }} title="Hasta" />

          {/* Text search */}
          <form onSubmit={e => { e.preventDefault(); setFText(textInput.trim()); setPage(1); }} style={{ display: 'flex', gap: 4 }}>
            <input
              type="search" placeholder="Buscar en comentarios…" value={textInput}
              onChange={e => { setTextInput(e.target.value); if (!e.target.value) { setFText(''); setPage(1); } }}
              style={{ ...inputStyle, minWidth: 180 }}
            />
          </form>

          {hasFilters && (
            <button type="button" onClick={clearFilters}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid #e0e0e0', background: '#fff', color: '#9ca3af', fontSize: 12, cursor: 'pointer' }}>
              Limpiar
            </button>
          )}

          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9ca3af' }}>
            {loading ? 'Cargando…' : `${total} resultado${total !== 1 ? 's' : ''}`}
          </span>
        </div>

        {/* ── Tabs ───────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 12 }}>
          {(['list', 'map'] as const).map(tab => (
            <button key={tab} type="button" onClick={() => setActiveTab(tab)}
              style={{
                padding: '8px 18px', borderRadius: '10px 10px 0 0',
                border: '1.5px solid', borderBottom: activeTab === tab ? '1.5px solid #fff' : '1.5px solid #f0f0f0',
                borderColor: activeTab === tab ? '#f0f0f0' : '#f0f0f0',
                background: activeTab === tab ? '#fff' : '#f5f7fa',
                color: activeTab === tab ? '#1a1a2e' : '#6b7280',
                fontWeight: activeTab === tab ? 700 : 500, fontSize: 13, cursor: 'pointer',
                position: 'relative', bottom: -1,
              }}>
              {tab === 'list' ? `Aportes (${total})` : 'Mapa analítico'}
            </button>
          ))}
        </div>

        {/* ── Tab: list ──────────────────────────────────────────────────────── */}
        {activeTab === 'list' && (
          <>
            <div style={{ background: '#fff', borderRadius: '0 14px 14px 14px', border: '1.5px solid #f0f0f0', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #f0f0f0' }}>
                    {['Fecha', 'Escala', 'Categoría', 'Ámbito', 'Estado', 'Calle', 'Contacto', ''].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && !loading && (
                    <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Sin resultados para los filtros aplicados</td></tr>
                  )}
                  {rows.map(row => (
                    <tr key={row.id} onClick={() => openDetail(row)}
                      style={{
                        borderBottom: '1px solid #f5f5f5', cursor: 'pointer',
                        background: selected?.id === row.id ? '#eff6ff' : '#fff',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => { if (selected?.id !== row.id) (e.currentTarget as HTMLTableRowElement).style.background = '#f8fafc'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = selected?.id === row.id ? '#eff6ff' : '#fff'; }}
                    >
                      <td style={{ padding: '9px 14px', color: '#6b7280', whiteSpace: 'nowrap', fontSize: 12 }}>{fmtShortDate(row.created_at)}</td>
                      <td style={{ padding: '9px 14px', fontSize: 12 }}>{SCALE_LABELS[row.scale] ?? row.scale}</td>
                      <td style={{ padding: '9px 14px', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: categoryColor(row.category), marginRight: 6 }} />
                        {row.category}
                      </td>
                      <td style={{ padding: '9px 14px', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11.5, color: '#6b7280' }}>
                        {row.zone_name ?? '—'}
                      </td>
                      <td style={{ padding: '9px 14px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: STATUS_COLORS[row.status] + '22', color: STATUS_COLORS[row.status] }}>
                          {STATUS_LABELS[row.status] ?? row.status}
                        </span>
                      </td>
                      <td style={{ padding: '9px 14px', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11.5, color: '#6b7280' }}>
                        {row.street_label ?? '—'}
                      </td>
                      <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                        {row.contact_requested ? <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 13 }}>✓</span> : <span style={{ color: '#e0e0e0' }}>—</span>}
                      </td>
                      <td style={{ padding: '9px 14px', color: '#c4c9d4', fontSize: 12 }}>Ver →</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                <button type="button" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid #e0e0e0', background: '#fff', cursor: page > 1 ? 'pointer' : 'not-allowed', color: page > 1 ? '#1a1a2e' : '#c4c9d4', fontSize: 13 }}>
                  ← Anterior
                </button>
                <span style={{ padding: '7px 14px', fontSize: 13, color: '#6b7280', alignSelf: 'center' }}>{page} / {totalPages}</span>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid #e0e0e0', background: '#fff', cursor: page < totalPages ? 'pointer' : 'not-allowed', color: page < totalPages ? '#1a1a2e' : '#c4c9d4', fontSize: 13 }}>
                  Siguiente →
                </button>
              </div>
            )}
          </>
        )}

        {/* ── Tab: map ───────────────────────────────────────────────────────── */}
        {activeTab === 'map' && (
          <div style={{ background: '#fff', borderRadius: '0 14px 14px 14px', border: '1.5px solid #f0f0f0', padding: '20px 20px 24px' }}>
            <AnalyticsMap
              projectId={projectId}
              filters={mapFilters}
              onSelectContribution={id => {
                const row = rows.find(r => r.id === id);
                if (row) {
                  openDetail(row);
                } else {
                  // Contribution not in current page — fetch full detail if needed
                  void adminFetchContributions(projectId, { page: 1, pageSize: 1 })
                    .then(() => undefined);
                }
              }}
            />
            <p style={{ margin: '16px 0 0', fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
              El mapa muestra hasta 2,000 aportes georreferenciados según los filtros activos. Haz click en un punto para ver el detalle.
            </p>
          </div>
        )}
      </div>

      {/* ── Detail panel ───────────────────────────────────────────────────────── */}
      {selected && (
        <DetailPanel
          row={selected}
          projectId={projectId}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}
